/**
 * Alt+C: create a customer or supplier mid-entry without losing the
 * voucher. This is one of the biggest quality-of-life wins in Tally and
 * almost nobody else implements it.
 */
import { useEffect, useState } from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Stack, TextField, Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { createParty } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { useLedgers } from '../store/LedgerCache'
import type { Ledger } from '../api/types'

interface Props {
  open: boolean
  initialName?: string
  defaultKind?: 'customer' | 'supplier'
  /** Only this kind (a subcontractor is always someone we pay). */
  fixedKind?: boolean
  /** Heading instead of "New party". */
  title?: string
  onClose: () => void
  onCreated: (led: Ledger) => void
}

export default function CreatePartyDialog(
  { open, initialName = '', defaultKind = 'customer', fixedKind = false, title,
    onClose, onCreated }: Props,
) {
  const { t } = useTranslation()
  const { add, ledgers } = useLedgers()
  const [kind, setKind] = useState<'customer' | 'supplier'>(defaultKind)
  const [name, setName] = useState(initialName)
  const [code, setCode] = useState('')
  const [creditDays, setCreditDays] = useState('30')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Suggest the next free code so the user does not have to invent one.
  useEffect(() => {
    if (!open) return
    setName(initialName)
    setKind(defaultKind)
    setError(null)
    const prefix = defaultKind === 'customer' ? 'D' : 'C'
    const used = ledgers
      .filter((l) => l.code.startsWith(prefix) && /^\w\d+$/.test(l.code))
      .map((l) => parseInt(l.code.slice(1), 10))
      .filter((n) => !Number.isNaN(n))
    const next = (used.length ? Math.max(...used) : 0) + 1
    setCode(`${prefix}${String(next).padStart(4, '0')}`)
  }, [open, initialName, defaultKind, ledgers])

  useEffect(() => {
    const prefix = kind === 'customer' ? 'D' : 'C'
    setCode((cur) => (cur ? prefix + cur.slice(1) : cur))
  }, [kind])

  async function save() {
    setBusy(true); setError(null)
    try {
      const led = await createParty({
        code, name_en: name, kind, credit_days: Number(creditDays) || 30,
      })
      add(led)          // into the cache immediately -- no refetch needed
      onCreated(led)
      onClose()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{title ?? t('entry.newParty')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {!fixedKind && (
            <TextField select label={t('entry.partyKind')} value={kind}
              onChange={(e) => setKind(e.target.value as 'customer' | 'supplier')}>
              <MenuItem value="customer">{t('entry.customer')}</MenuItem>
              <MenuItem value="supplier">{t('entry.supplier')}</MenuItem>
            </TextField>
          )}
          <TextField label={t('entry.name')} value={name} autoFocus
            onChange={(e) => setName(e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label={t('entry.code')} value={code}
              onChange={(e) => setCode(e.target.value)} sx={{ flex: 1 }} />
            <TextField label={t('entry.creditDays')} value={creditDays}
              inputMode="numeric"
              onChange={(e) => setCreditDays(e.target.value.replace(/\D/g, ''))}
              sx={{ width: 120 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
        <Button variant="contained" onClick={save}
          disabled={busy || !name.trim() || !code.trim()}>
          {busy ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
